import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import * as Sentry from '@sentry/vue'
import axios from 'axios'
import './registerServiceWorker'

import './assets/fonts/icons.js'
import './services/GlobalVariables'
import './services/ProxyHandler'


import 'bootstrap/dist/js/bootstrap.bundle.min'
import 'bootstrap/dist/css/bootstrap.min.css';
import DrawerLayout from 'vue-drawer-layout'

import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

Vue.component('loading', Loading);
Vue.use(DrawerLayout);
Vue.config.productionTip = false

/**
 * Strips known PII keys from event bodies before they leave the device.
 * Defence-in-depth on top of `dataCollection.userInfo: false` so a future
 * config change cannot accidentally leak credentials to Sentry.
 * @param {import("@sentry/vue").Event} event - the Sentry event
 * @returns {import("@sentry/vue").Event | null} redacted event, or null to drop
 */
function redactPII(event) {
  if (!event) return event;
  const dropKeys = ["pin", "password", "creditCard", "credit_card", "token", "authorization"];
  const scrub = (obj) => {
    if (!obj || typeof obj !== "object") return obj;
    for (const k of Object.keys(obj)) {
      if (dropKeys.includes(k.toLowerCase())) {
        obj[k] = "[REDACTED]";
      } else if (typeof obj[k] === "object") {
        scrub(obj[k]);
      }
    }
    return obj;
  };
  return scrub(event);
}

Sentry.init({
  Vue,
  dsn: process.env.VUE_APP_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  enableLogs: true,
  release: process.env.VUE_APP_SENTRY_RELEASE,
  environment: process.env.VUE_APP_SENTRY_ENVIRONMENT || "production",
  dataCollection: {
    userInfo: false,
  },
  beforeSend: redactPII,
});

/**
 * Single point of coverage for axios errors -> Sentry.
 *
 * Supervisor App has 4 files that `import axios from "axios"` and every one
 * uses the DEFAULT axios instance (no `axios.create` anywhere in src/ —
 * verified by grep). A single `axios.interceptors.response.use(...)`
 * registered here catches errors from every consumer without editing the
 * per-service `.catch()` blocks.
 *
 * Without this, axios `.catch()` blocks swallow errors before Sentry's
 * `window.onerror` / `unhandledrejection` / `Vue.config.errorHandler`
 * can see them — CORS, 500s, offline failures silently never reach Sentry.
 *
 * Must be registered AFTER `Sentry.init({...})` so `captureException` is
 * wired up. Re-throws via `Promise.reject(error)` so existing `.catch()`
 * handlers still run.
 */
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    Sentry.captureException(error, {
      extra: {
        axiosUrl: error?.config?.url,
        axiosMethod: error?.config?.method?.toUpperCase(),
        axiosStatus: error?.response?.status,
        axiosBaseURL: error?.config?.baseURL,
      },
    });
    return Promise.reject(error);
  }
);

new Vue({
  router,
  store,
  render: function (h) { return h(App) }
}).$mount('#app')
